import { motion } from "framer-motion";
import {
  Users, ShoppingBag, Calendar, Briefcase, Heart, Wallet,
  GraduationCap, BookOpen, Car, Home, Brain, PlaySquare,
  Vote, Truck, TrendingUp, UtensilsCrossed, HandHeart, Video,
} from "lucide-react";

const features = [
  { icon: Users, title: "Your Whole Social Life", desc: "Post, chat, group up — keep every friend in one feed.", color: "text-primary" },
  { icon: PlaySquare, title: "Video & LIVE Streaming", desc: "Short videos, solo & multi-guest lives, gifts and replays.", color: "text-destructive" },
  { icon: ShoppingBag, title: "Buy & Sell Anything", desc: "Marketplace, auctions and instant escrow built in.", color: "text-secondary" },
  { icon: Briefcase, title: "Land Work That Pays", desc: "Jobs, gigs and freelance contracts in 35+ fields.", color: "text-primary" },
  { icon: Calendar, title: "Run Unforgettable Events", desc: "Create, sell tickets and check guests in instantly.", color: "text-accent" },
  { icon: Heart, title: "Meet Someone Real", desc: "Private matching with verified, intent-led profiles.", color: "text-destructive" },
  { icon: Wallet, title: "One Wallet, Every Payment", desc: "Multi-currency wallet, airtime, eSIM, gift cards and bills.", color: "text-accent" },
  { icon: GraduationCap, title: "Win at Campus Life", desc: "Study tools, library, quizzes and student crews.", color: "text-primary" },
  { icon: Brain, title: "AI That Works for You", desc: "Resume builder, interview questions and more.", color: "text-secondary" },
  { icon: BookOpen, title: "Faith & Daily Wisdom", desc: "Bible wall, sermons, prayer with Emmanuel AI.", color: "text-accent" },
  { icon: Car, title: "Move, Park, Repair", desc: "Auto listings, mechanics and spare parts nearby.", color: "text-primary" },
  { icon: Home, title: "Find Your Next Home", desc: "Properties, short stays and verified landlords.", color: "text-accent" },
  { icon: Truck, title: "Peer-to-Peer Logistics", desc: "Drivers and goods owners agree directly, with live GPS tracking.", color: "text-secondary" },
  { icon: Vote, title: "Election Tracker", desc: "Citizen result reporting, verification maps and leaderboards.", color: "text-primary" },
  { icon: TrendingUp, title: "Trading & Markets", desc: "Forex suite, signals, price flip and the virtual market.", color: "text-accent" },
  { icon: UtensilsCrossed, title: "Food Court & Meals", desc: "Order from vendors and plan weekly meals with AI.", color: "text-secondary" },
  { icon: Video, title: "Video Meetings", desc: "HD calls, scheduling, invites, reactions and in-call chat.", color: "text-primary" },
  { icon: HandHeart, title: "Giving Hub", desc: "Donate to verified organizations in seconds.", color: "text-destructive" },
];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.03 } },
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

const FeaturesSection = () => {
  return (
    <section className="py-8 md:py-24 bg-muted/30 mt-0">
      <div className="container px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-6 md:mb-16"
        >
          <h2 className="font-display text-2xl md:text-5xl font-bold mb-2 md:mb-4">
            Everything You Need, <span className="gradient-text">One Platform</span>
          </h2>
          <p className="text-sm md:text-lg text-muted-foreground max-w-2xl mx-auto">
            70+ features designed to connect, create, earn, learn, and grow — all in one place.
          </p>
        </motion.div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 md:gap-4 px-2 md:px-0"
        >
          {features.map((f) => (
            <motion.div
              key={f.title}
              variants={item}
              className="group relative bg-card rounded-2xl p-3 md:p-5 border border-border/60 hover:border-primary/40 hover:shadow-card-hover transition-all duration-300 cursor-default"
            >
              <div className="w-9 h-9 md:w-11 md:h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-2 md:mb-3 group-hover:scale-110 transition-transform">
                <f.icon className={`h-5 w-5 md:h-6 md:w-6 ${f.color}`} />
              </div>
              <h3 className="font-display font-bold text-xs md:text-sm mb-0.5 md:mb-1">{f.title}</h3>
              <p className="text-[10px] md:text-xs text-muted-foreground leading-tight">{f.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturesSection;
